import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TrendingUp, Flame, Trophy, MapPin, Calendar } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Alert, AlertDescription } from '../components/ui/alert';
import BadgeCard from '../components/badges/BadgeCard';
import { useRoleBasedApi } from '../hooks/useRoleBasedApi';

interface StatisticsData {
  dip_statistics: {
    total_dips: number;
    this_week_dips: number;
    this_month_dips: number;
    unique_locations: number;
    average_dips_per_week: number;
  };
  streaks: {
    current_daily_streak: number;
    max_daily_streak: number;
    current_weekly_streak: number;
    max_weekly_streak: number;
  };
  badges: {
    recent_badges: Badge[];
    total_badges: number;
  };
}

interface Badge {
  name: string;
  display_name: string;
  description: string;
  icon: string;
  color: string;
  category: string;
  earned_at: string;
}

const StatisticsPage: React.FC = () => {
  const [stats, setStats] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { get } = useRoleBasedApi();

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await get('/statistics');
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" variant="default" />
        <p className="mt-4 text-sauna-navy font-medium">Loading statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No statistics available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <TrendingUp className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Your Statistics</h1>
      </div>

      {/* Dip Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🌊 Dip Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatItem 
              label="Total Dips" 
              value={stats.dip_statistics.total_dips}
              icon="🌊"
            />
            <StatItem 
              label="This Week" 
              value={stats.dip_statistics.this_week_dips}
              icon="📅"
            />
            <StatItem 
              label="This Month" 
              value={stats.dip_statistics.this_month_dips}
              icon="📊"
            />
            <StatItem 
              label="Locations" 
              value={stats.dip_statistics.unique_locations}
              icon="🗺️"
            />
            <StatItem 
              label="Avg/Week" 
              value={stats.dip_statistics.average_dips_per_week.toFixed(1)}
              icon="📈"
            />
          </div>
        </CardContent>
      </Card>

      {/* Streaks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-500" />
            Streaks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StreakCard 
              label="Current Daily" 
              value={stats.streaks.current_daily_streak}
              maxValue={stats.streaks.max_daily_streak}
              icon="🔥"
            />
            <StreakCard 
              label="Max Daily" 
              value={stats.streaks.max_daily_streak}
              isMax={true}
              icon="🏆"
            />
            <StreakCard 
              label="Current Weekly" 
              value={stats.streaks.current_weekly_streak}
              maxValue={stats.streaks.max_weekly_streak}
              icon="📅"
            />
            <StreakCard 
              label="Max Weekly" 
              value={stats.streaks.max_weekly_streak}
              isMax={true}
              icon="🎯"
            />
          </div>
        </CardContent>
      </Card>

      {/* Recent Badges */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Recent Badges ({stats.badges.total_badges} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.badges.recent_badges.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🏅</div>
              <p className="text-gray-600">No badges earned yet. Keep dipping to earn your first badge!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {stats.badges.recent_badges.map((badge) => (
                <BadgeCard 
                  key={badge.name} 
                  badge={badge}
                  showDescription={false}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Stat Item Component
const StatItem: React.FC<{ label: string; value: string | number; icon: string }> = ({ 
  label, 
  value, 
  icon 
}) => {
  return (
    <div className="text-center p-4 bg-gray-50 rounded-lg">
      <div className="text-2xl mb-2">{icon}</div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      <div className="text-sm text-gray-600">{label}</div>
    </div>
  );
};

// Streak Card Component
const StreakCard: React.FC<{ 
  label: string; 
  value: number; 
  maxValue?: number;
  isMax?: boolean;
  icon: string;
}> = ({ label, value, maxValue, isMax = false, icon }) => {
  const percentage = maxValue ? (value / maxValue) * 100 : 0;
  
  return (
    <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-lg border border-orange-200">
      <div className="text-2xl mb-2">{icon}</div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      <div className="text-sm text-gray-600 mb-2">{label}</div>
      
      {!isMax && maxValue && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-orange-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      )}
      
      {!isMax && maxValue && (
        <div className="text-xs text-gray-500 mt-1">
          {percentage.toFixed(0)}% of max
        </div>
      )}
    </div>
  );
};

export default StatisticsPage; 